import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useSEO } from '@/lib/seo';
import { APP } from '@/constants/brand';
import { Wrench, Loader2, KeyRound, ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle } from 'lucide-react';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { finishReset } = useAuth();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useSEO({
    title: 'Choose New Password — DIY X-treme',
    description: 'Set your new DIY X-treme password to get back into your account.',
    canonical: 'https://diybot.org/auth/reset',
  });

  useEffect(() => {
    if (!token) setError('This reset link is missing its token. Please request a new one.');
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/reset-password', { token, new_password: password });
      // finishReset stores the token + user and logs the user in.
      if (typeof finishReset === 'function') finishReset(res.data);
      setDone(true);
      setTimeout(() => navigate('/app', { replace: true }), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not reset password. Try requesting a new link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 grid-bg relative">
      <button
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
        data-testid="reset-back-btn"
        className="absolute top-4 left-4 inline-flex items-center gap-1 text-zinc-400 hover:text-amber-500 border border-zinc-800 hover:border-amber-500 px-3 py-2 font-mono text-xs uppercase tracking-widest bg-zinc-950/70 backdrop-blur transition-colors z-10"
        aria-label="Back"
      >
        <ArrowLeft size={14} /> <span>Back</span>
      </button>

      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-11 h-11 bg-amber-500 flex items-center justify-center">
            <Wrench size={22} className="text-zinc-950" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="font-display text-3xl text-zinc-100 tracking-wide leading-none">{APP.name}</h1>
            <div className="font-mono text-[10px] text-amber-500 uppercase tracking-[0.2em] mt-1">
              {APP.tagline}
            </div>
          </div>
        </div>

        <div className="industrial-card border-t-4 border-t-amber-400">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-2 flex items-center gap-2">
            <KeyRound size={12} /> Set new password
          </div>
          <h2 className="font-display text-3xl text-zinc-100 tracking-tight mb-4">Almost there.</h2>

          {done ? (
            <div data-testid="reset-success" className="text-center py-4">
              <CheckCircle2 size={40} className="text-lime-400 mx-auto mb-3" />
              <div className="font-display text-2xl text-zinc-100 mb-2">Password updated</div>
              <p className="text-zinc-400 text-sm">Signing you in…</p>
            </div>
          ) : (
            <>
              <p className="text-zinc-400 text-sm mb-6">
                Pick a new password (8+ characters). We'll log you right in.
              </p>
              <form onSubmit={submit} className="space-y-3">
                <input
                  data-testid="reset-password"
                  type="password"
                  required
                  autoFocus
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="industrial-input"
                  placeholder="New password"
                  minLength={8}
                />
                <input
                  data-testid="reset-confirm"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="industrial-input"
                  placeholder="Confirm new password"
                  minLength={8}
                />
                {error && (
                  <div data-testid="reset-error" className="border border-red-500/40 bg-red-500/10 text-red-400 px-3 py-2 text-sm font-mono flex items-center gap-2">
                    <AlertTriangle size={14} /> {error}
                  </div>
                )}
                <button
                  type="submit"
                  data-testid="reset-submit"
                  disabled={loading || !token}
                  className="btn-primary w-full disabled:opacity-50"
                >
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Resetting…</> : <>Set password & log in <ArrowRight size={16} /></>}
                </button>
              </form>
            </>
          )}

          <div className="mt-6 border-t border-zinc-800 pt-4 text-center">
            <Link to="/auth/forgot" data-testid="reset-request-new" className="text-zinc-400 hover:text-amber-500 text-sm font-mono uppercase tracking-widest">
              Need a new reset link?
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
